import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './RenameDialog.css';

interface RenameDialogProps {
  /** Heading, e.g. "Rename segment". */
  title: string;
  value: string;
  onChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** Accessible name of the text field. */
  inputLabel?: string;
  /** Accessible name of the OK button. */
  confirmLabel?: string;
  confirmText?: string;
  /** OK is disabled while busy or while the name is empty. */
  busy?: boolean;
  error?: string | null;
  inputRef?: React.Ref<HTMLInputElement>;
}

// Events that must not leak from the dialog to whatever sits behind it in the React tree (rows,
// cards and the video all have their own click/tap/key handlers).
const stopEvent = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * Full-screen rename dialog: a backdrop freezes the page until the user presses OK or Cancel, so
 * typing on a phone can never tap the video or a row underneath. Rendered into the fullscreen
 * element when there is one (otherwise into <body>) so it also shows in fullscreen.
 */
const RenameDialog: React.FC<RenameDialogProps> = ({
  title,
  value,
  onChange,
  onConfirm,
  onCancel,
  inputLabel = 'Name',
  confirmLabel,
  confirmText = 'OK',
  busy = false,
  error = null,
  inputRef
}) => {
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const canConfirm = !busy && value.trim().length > 0;

  useEffect(() => {
    const input = localInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  const setInputRef = (element: HTMLInputElement | null) => {
    localInputRef.current = element;
    if (typeof inputRef === 'function') {
      inputRef(element);
    } else if (inputRef && typeof inputRef === 'object') {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = element;
    }
  };

  const container = (typeof document !== 'undefined' && document.fullscreenElement) || document.body;

  return createPortal(
    <div
      className="rename-dialog-backdrop"
      onClick={stopEvent}
      onPointerDown={stopEvent}
      onPointerUp={stopEvent}
      onMouseDown={stopEvent}
      onTouchStart={stopEvent}
      onTouchEnd={stopEvent}
      onKeyDown={stopEvent}
      onKeyUp={stopEvent}
      onContextMenu={stopEvent}
    >
      <form
        className="rename-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={(event) => {
          event.preventDefault();
          if (canConfirm) onConfirm();
        }}
      >
        <h2 className="rename-dialog-title">{title}</h2>
        <input
          ref={setInputRef}
          className="rename-dialog-input"
          type="text"
          value={value}
          aria-label={inputLabel}
          autoComplete="off"
          enterKeyHint="done"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onCancel();
            }
          }}
        />
        {error && <p className="rename-dialog-error" role="alert">{error}</p>}
        <div className="rename-dialog-actions">
          <button type="button" className="rename-dialog-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="rename-dialog-button rename-dialog-button-primary"
            disabled={!canConfirm}
            aria-label={confirmLabel}
          >
            {confirmText}
          </button>
        </div>
      </form>
    </div>,
    container
  );
};

export default RenameDialog;
